"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ActivityIcon } from "lucide-react";
import { Button } from "@ui/components/button";
import { Calendar } from "@ui/components/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/components/select";
import { DateRange as DayPickerDateRange } from "react-day-picker";

export type TimeRangeType = "live" | "24h" | "7d" | "30d" | "90d" | "custom";

export interface DateRange {
  from: Date;
  to: Date;
}

export interface DateRangeFilterProps {
  timeRange: TimeRangeType;
  onTimeRangeChange: (value: TimeRangeType) => void;
  customDateRange?: DateRange;
  onCustomDateRangeChange: (value: DateRange | undefined) => void;
  isLoading?: boolean;
  isLive?: boolean;
  showLiveOption?: boolean;
  translationNamespace?: string;
}

// Helper function to safely use translations with dynamic namespace
function useSafeTranslation(namespace: string) {
  // Use any to bypass the type checking for the namespace
  const t = useTranslations(namespace as any);
  // Return a function that safely accesses translations
  return (key: string, params?: Record<string, any>) => t(key as never, params);
}

export function DateRangeFilter({
  timeRange,
  onTimeRangeChange,
  customDateRange,
  onCustomDateRangeChange,
  isLoading = false,
  isLive = false,
  showLiveOption = true,
}: DateRangeFilterProps) {
  // Use both namespace-specific translations and common translations
  const t = useTranslations('common');
  
  // State for date picker
  const [date, setDate] = useState<DayPickerDateRange | undefined>(
    customDateRange 
      ? { from: customDateRange.from, to: customDateRange.to }
      : undefined
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // Update date range when timeRange changes
  useEffect(() => {
    if (timeRange !== "custom") {
      setDate(undefined);
      onCustomDateRangeChange(undefined);
    }
  }, [timeRange, onCustomDateRangeChange]);
  
  // Handle custom date range selection
  const handleSelectTimeRange = (value: string) => {
    const newTimeRange = value as TimeRangeType;
    onTimeRangeChange(newTimeRange);
    
    if (newTimeRange === "custom") {
      setIsCalendarOpen(true);
    }
  };
  
  // Apply custom date range
  const handleApplyDateRange = () => {
    if (date?.from && date?.to) {
      onCustomDateRangeChange({
        from: date.from,
        to: date.to
      });
      onTimeRangeChange("custom");
      setIsCalendarOpen(false);
    }
  };
  
  // Reset date range
  const handleResetDateRange = () => {
    setDate(undefined);
    onCustomDateRangeChange(undefined);
    onTimeRangeChange("30d");
    setIsCalendarOpen(false);
  };
  
  // Format date range for display
  const formatDateRange = () => {
    if (!customDateRange) return "";
    
    const fromDate = format(customDateRange.from, "MMM d, yyyy");
    const toDate = format(customDateRange.to, "MMM d, yyyy");
    
    return `${fromDate} - ${toDate}`;
  };
  
  return (
    <div className="flex items-center gap-3">
      <Select
        value={timeRange}
        onValueChange={handleSelectTimeRange}
        disabled={isLoading}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Select time range" />
        </SelectTrigger>
        <SelectContent>
          {showLiveOption && <SelectItem value="live">{t("timeRanges.live")}</SelectItem>}
          <SelectItem value="24h">{t("timeRanges.24h")}</SelectItem>
          <SelectItem value="7d">{t("timeRanges.7d")}</SelectItem>
          <SelectItem value="30d">{t("timeRanges.30d")}</SelectItem>
          <SelectItem value="90d">{t("timeRanges.90d")}</SelectItem>
          <SelectItem value="custom">{t("timeRanges.custom")}</SelectItem>
        </SelectContent>
      </Select>
      
      {isLive && (
        <div className="flex items-center gap-2">
          <ActivityIcon className="h-4 w-4 text-green-500 animate-pulse" />
          <span className="text-sm text-muted-foreground">
            {t("dateFilters.liveData")}
          </span>
        </div>
      )}

      {/* Custom date range picker */}
      {timeRange === "custom" && (
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="justify-start text-left font-normal min-w-[240px]"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {customDateRange ? formatDateRange() : "Select date range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 border-b">
              <div className="text-sm font-medium">
                {t("dateFilters.selectDateRange")}
              </div>
            </div>
            <Calendar
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={(value) => setDate(value)}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
            <div className="flex items-center justify-between p-3 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetDateRange}
              >
                {t("dateFilters.resetFilters")}
              </Button>
              <Button
                size="sm"
                onClick={handleApplyDateRange}
                disabled={!date?.from || !date?.to}
              >
                {t("dateFilters.apply")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
} 